import React, { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { RootState } from '../types';

const LoginPage: React.FC = () => {
  const { isLoading, error } = useSelector((state: RootState) => state.auth);
  const auth = useAuth();
  // Read once per mount: the effect below can re-run (StrictMode, auth state
  // changes), and re-reading sessionStorage after the flag was cleared would
  // auto-redirect straight back into Cognito right after logout.
  const justLoggedOut = useRef(sessionStorage.getItem('justLoggedOut') === 'true');

  // Automatically trigger Cognito sign-in on mount (unless just logged out)
  useEffect(() => {
    if (auth.isLoading) return;

    if (justLoggedOut.current) {
      sessionStorage.removeItem('justLoggedOut');
      return;
    }

    if (!auth.isAuthenticated && !auth.error) {
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error]);

  // Handle Cognito sign-in
  const handleCognitoSignIn = () => {
    auth.signinRedirect();
  };
  
  // Show loading state if Cognito is loading
  if (auth.isLoading || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in to CyberDash
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          
          {auth.error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {auth.error.message}
            </Alert>
          )}
          
          <Box sx={{ mt: 3, width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              onClick={handleCognitoSignIn}
              disabled={auth.isLoading}
            >
              {auth.isLoading ? <CircularProgress size={24} /> : 'Sign In with Cognito'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;